import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import { PiSpinnerGapLight, PiWarningLight } from 'react-icons/pi';
import Spinner from '../shared/TailwindComponents/Spinner';

interface DeleteProjectConfirmationProps {
  projectCount?: number;
  projectName?: string;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

const DeleteProjectConfirmation: React.FC<DeleteProjectConfirmationProps> = ({
  projectCount = 1,
  projectName,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);

  const isPlural = projectCount > 1;

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } catch {
      setIsDeleting(false);
    }
  };

  if (isDeleting) {
    return (
      <div className="flex flex-col items-center justify-center py-10 px-4 gap-4">
        <div className="relative w-12 h-12">
          <Spinner size="glyph" className="!h-12 !w-12" />
        </div>
        <div className="text-center space-y-1.5">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {isPlural
              ? t('Deleting {{count}} projects...', { count: projectCount })
              : t('Deleting project...')}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('Please wait, this may take a moment.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="space-y-3 py-1 p-4">
        <div className="flex items-start gap-3 p-4 rounded-lg border border-warning-200 dark:border-warning-800/50 bg-warning-50 dark:bg-warning-900/10">
          <PiWarningLight className="w-5 h-5 text-warning-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-gray-900 dark:text-gray-100">
              {isPlural
                ? t('You are about to delete {{count}} projects.', { count: projectCount })
                : projectName
                  ? t('You are about to delete "{{name}}".', { name: projectName })
                  : t('You are about to delete this project.')}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
              {t('This will permanently remove the project and all its resource links. The linked resources themselves (matters, tasks, files, etc.) will not be deleted.')}
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-4 pt-6 mt-2 border-t border-gray-300 dark:border-gray-700 px-4">
        <Button variant="neutral" size="small" onClick={onCancel} disabled={isDeleting} className="w-24">
          {t('Cancel')}
        </Button>
        <Button variant="destructive" size="small" onClick={handleConfirm} disabled={isDeleting} className="w-24">
          {t('Delete')}
        </Button>
      </div>
    </div>
  );
};

export default DeleteProjectConfirmation;
